import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useChat } from '../contexts/ChatContext';
import { chatApi } from '../services/chatApi';
import { friendshipApi } from '../services/friendshipApi';
import Avatar from './Avatar';
import { IoClose, IoPersonAdd, IoPersonRemove, IoExit, IoSearch, IoArrowBack } from 'react-icons/io5';
import './GroupInfoModal.css';

const GroupInfoModal = ({ conversation, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const { loadConversations } = useChat();
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [friends, setFriends] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState(null);
  const [transferring, setTransferring] = useState(false);

  const currentUserId = conversation.participants?.find(p => p.user_id === conversation.created_by)?.user_id;
  const isAdmin = currentUserId === conversation.created_by;

  useEffect(() => {
    loadFriends();
  }, []);

  const loadFriends = async () => {
    try {
      const response = await friendshipApi.getFriends();
      if (response.success) {
        setFriends(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current participant IDs
  const currentParticipantIds = (conversation.participants || []).map(p => p.user_id);

  // Filter friends who are not already in the group
  const availableFriends = friends.filter(friend =>
    !currentParticipantIds.includes(friend.id) &&
    (friend.display_name || friend.username || '')
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const handleAddMembers = async (friend) => {
    setAdding(true);
    try {
      await chatApi.addParticipants(conversation.id, [friend.id]);
      await loadConversations();
      onUpdated?.();
    } catch (error) {
      console.error('Failed to add member:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(t('chat.confirmRemoveMember'))) return;

    setRemoving(userId);
    try {
      await chatApi.removeParticipant(conversation.id, userId);
      await loadConversations();
      onUpdated?.();
    } catch (error) {
      console.error('Failed to remove member:', error);
    } finally {
      setRemoving(null);
    }
  };

  const handleLeaveGroup = async () => {
    if (isAdmin) {
      // Show transfer ownership modal instead
      setShowTransferModal(true);
      return;
    }

    if (!window.confirm(t('chat.confirmLeaveGroup'))) return;

    setLeaving(true);
    try {
      await chatApi.removeParticipant(conversation.id, currentUserId);
      await loadConversations();
      onClose();
    } catch (error) {
      console.error('Failed to leave group:', error);
    } finally {
      setLeaving(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewAdmin) return;

    setTransferring(true);
    try {
      // Transfer ownership by updating the conversation
      await chatApi.transferOwnership(conversation.id, selectedNewAdmin);
      // Then leave the group
      await chatApi.removeParticipant(conversation.id, currentUserId);
      await loadConversations();
      onClose();
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
    } finally {
      setTransferring(false);
      setShowTransferModal(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get other members (excluding current user) for transfer selection
  const otherMembers = (conversation.participants || []).filter(
    p => p.user_id !== currentUserId
  );

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="group-info-modal">
        {/* Transfer Ownership Modal */}
        {showTransferModal && (
          <div className="transfer-modal">
            <div className="transfer-modal-header">
              <h3>{t('chat.transferOwnership')}</h3>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>
                <IoClose />
              </button>
            </div>
            <div className="transfer-modal-content">
              <p className="transfer-description">
                {t('chat.transferOwnershipDescription')}
              </p>
              <div className="transfer-members-list">
                {otherMembers.map(participant => (
                  <div
                    key={participant.user_id}
                    className={`transfer-member-item ${selectedNewAdmin === participant.user_id ? 'selected' : ''}`}
                    onClick={() => setSelectedNewAdmin(participant.user_id)}
                  >
                    <Avatar user={participant.user} size="medium" />
                    <div className="transfer-member-info">
                      <span className="transfer-member-name">
                        {participant.user?.display_name || 'Unknown'}
                      </span>
                    </div>
                    {selectedNewAdmin === participant.user_id && (
                      <div className="transfer-checkmark">✓</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="transfer-modal-actions">
              <button
                className="transfer-cancel-btn"
                onClick={() => setShowTransferModal(false)}
                disabled={transferring}
              >
                {t('common.cancel')}
              </button>
              <button
                className="transfer-confirm-btn"
                onClick={handleTransferOwnership}
                disabled={!selectedNewAdmin || transferring}
              >
                {transferring ? <div className="btn-spinner"></div> : t('chat.transferAndLeave')}
              </button>
            </div>
          </div>
        )}

        {/* Header with cover */}
        <div className="group-info-header">
          <div className="group-info-cover">
            <button className="modal-close" onClick={onClose}>
              <IoClose />
            </button>
          </div>
          <div className="group-info-avatar">
            <Avatar user={null} groupName={conversation.name} size="large" />
          </div>
        </div>

        {/* Info body */}
        <div className="group-info-body">
          <h2 className="group-name">{conversation.name}</h2>
          <span className="group-member-count">
            {(conversation.participants || []).length} {t('chat.groupMembers').toLowerCase()}
          </span>
        </div>

        {/* Action buttons */}
        <div className="group-actions">
          <button
            className="group-action-btn"
            onClick={() => setShowAddMembers(true)}
          >
            <IoPersonAdd style={{ color: '#333' }} />
            {t('chat.addMembers')}
          </button>
          <button
            className="group-action-btn danger"
            onClick={handleLeaveGroup}
            disabled={leaving}
          >
            {leaving ? <div className="btn-spinner"></div> : <IoExit style={{ color: '#333' }} />}
            {t('chat.leaveGroup')}
          </button>
        </div>

        {/* Content */}
        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
          {showAddMembers ? (
            <>
              {/* Row 1: Back button and title */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--color-border, #dbdbdb)', width: '100%' }}>
                <button className="back-btn" onClick={() => setShowAddMembers(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid #333', borderRadius: '8px', background: 'white', color: '#333', cursor: 'pointer', flexShrink: 0 }}>
                  <span style={{ color: '#333', fontSize: '20px', lineHeight: '1' }}>&larr;</span>
                </button>
                <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text, #000)' }}>{t('chat.addMembers')}</span>
              </div>
              {/* Row 2: Search input */}
              <div style={{ position: 'relative', marginTop: '12px', marginBottom: '12px', width: '100%' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#333', pointerEvents: 'none', zIndex: 1, fontSize: '16px' }}>&#128269;</span>
                <input
                  type="text"
                  placeholder={t('chat.searchFriends')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '12px 12px 12px 40px', border: '1px solid var(--color-border, #dbdbdb)', borderRadius: '10px', background: 'var(--color-bg-secondary, #f0f2f5)', fontSize: '14px', color: 'var(--color-text, #000)', outline: 'none', boxSizing: 'border-box', height: '42px' }}
                />
              </div>
              <div className="add-friends-list">
                {loading ? (
                  <div className="modal-loading">
                    <div className="loading-spinner"></div>
                  </div>
                ) : availableFriends.length === 0 ? (
                  <div className="modal-empty">
                    <p style={{ color: 'var(--color-secondary, #737373)' }}>{search ? t('chat.noResults') : t('chat.noFriends')}</p>
                  </div>
                ) : (
                  availableFriends.map(friend => (
                    <div
                      key={friend.id}
                      className="friend-item"
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '12px', width: '100%', boxSizing: 'border-box', background: 'transparent' }}
                    >
                      <Link to={`/profile/${friend.id}`} onClick={(e) => {
                        // Delay the add member action to allow navigation
                        setTimeout(() => handleAddMembers(friend), 100);
                      }} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, textDecoration: 'none', color: 'inherit', minWidth: 0 }}>
                        <Avatar user={friend} size="medium" />
                        <span style={{ fontWeight: 500, color: 'var(--color-text, #000)', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{friend.display_name}</span>
                      </Link>
                      <button
                        className="friend-add-btn"
                        onClick={() => handleAddMembers(friend)}
                        disabled={adding}
                        style={{ width: '32px', height: '32px', border: '1px solid #333', borderRadius: '8px', background: 'white', color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        {adding ? <div className="btn-spinner"></div> : <span style={{ color: '#333', fontSize: '20px', fontWeight: '500', lineHeight: '1' }}>+</span>}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <div className="members-list">
                {(conversation.participants || []).map(participant => {
                  const isCreator = participant.user_id === conversation.created_by;

                  return (
                    <div key={participant.user_id} className="member-item">
                      <Link to={`/profile/${participant.user_id}`} className="member-link">
                        <Avatar user={participant.user} size="medium" />
                        <div className="member-info">
                          <span className="member-name">
                            {participant.user?.display_name || 'Unknown'}
                          </span>
                          {isCreator && (
                            <span className="member-badge">Admin</span>
                          )}
                        </div>
                      </Link>
                      {isAdmin && !isCreator && (
                        <button
                          className="member-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveMember(participant.user_id);
                          }}
                          disabled={removing === participant.user_id}
                          title={t('chat.removeMember')}
                        >
                          {removing === participant.user_id ? (
                            <div className="btn-spinner"></div>
                          ) : (
                            <IoPersonRemove />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupInfoModal;
